/**
 * The PDF templates, in the order pickers show them.
 *
 * Kept free of Vue/Nuxt imports: the server (export, preview, guest checkout)
 * and the client read the same list.
 */
export const PDF_TEMPLATES = ['basic', 'ats', 'classic', 'modern', 'minimal', 'executive', 'europass', 'creative'] as const
export type PdfTemplateId = typeof PDF_TEMPLATES[number]

/**
 * Exported without the watermark for everybody, no plan or credit needed
 * (decided 2026-09-23). It is plain on purpose — one column, no photo, no
 * decoration — and says "Made with InMyResume.com" at the bottom. Every other
 * template keeps the watermark on the free plan.
 */
export const FREE_CLEAN_TEMPLATE = 'basic' satisfies PdfTemplateId

/**
 * The layout offered next to Basic when its free download is clicked: "your
 * CV, as it is, for free, or like this, clean, for one payment". Modern looks
 * the least like Basic (dark sidebar, two columns) and needs no photo.
 */
export const DESIGN_UPSELL_TEMPLATE = 'modern' satisfies PdfTemplateId

export function isFreeCleanTemplate(template: string | null | undefined): boolean {
  return template === FREE_CLEAN_TEMPLATE
}

export function isPdfTemplate(template: unknown): template is PdfTemplateId {
  return typeof template === 'string' && (PDF_TEMPLATES as readonly string[]).includes(template)
}

/** What a template is like, for the filters on /templates and the pickers. */
export type TemplateTag = 'ats' | 'photo' | 'one-column' | 'two-column' | 'creative' | 'traditional'

export interface PdfTemplateMeta {
  tags: readonly TemplateTag[]
}

export const PDF_TEMPLATE_META: Record<PdfTemplateId, PdfTemplateMeta> = {
  basic: { tags: ['ats', 'one-column', 'traditional'] },
  ats: { tags: ['ats', 'photo', 'one-column'] },
  classic: { tags: ['photo', 'one-column', 'traditional'] },
  modern: { tags: ['photo', 'two-column', 'creative'] },
  minimal: { tags: ['photo', 'one-column'] },
  executive: { tags: ['ats', 'one-column', 'traditional'] },
  europass: { tags: ['photo', 'two-column', 'traditional'] },
  creative: { tags: ['photo', 'one-column', 'creative'] },
}

export const TEMPLATE_TAGS: readonly TemplateTag[] = ['ats', 'photo', 'one-column', 'two-column', 'creative', 'traditional']
